import { z } from 'zod';

import { PERMISSION } from '../../access-control/permission';
import { StaffRole } from '../../access-control/staff';
import { EmailStringSchema } from '../../common';
import { paginateQuerySchema, paginateResponseSchema } from '../common';

export enum StaffUserStatus {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  INVITED = 'invited',
}

export const StatusesSchema = z.nativeEnum(StaffUserStatus);
export type StatusesType = z.infer<typeof StatusesSchema>;

export const RolesSchema = z.nativeEnum(StaffRole);
export type RolesType = z.infer<typeof RolesSchema>;

export const StaffUserSchema = z.object({
  profilePicture: z
    .object({
      id: z.string(),
      uri: z.object({ original: z.string() }).and(z.record(z.string())),
    })
    .nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: EmailStringSchema,
  phoneNumber: z.string().nullable(),
  role: RolesSchema,
  status: StatusesSchema,
  permissions: z.array(z.nativeEnum(PERMISSION)),
  id: z.string().uuid(),
});
export type StaffUserType = z.infer<typeof StaffUserSchema>;

export const GetStaffUsersResponseSchema = paginateResponseSchema(StaffUserSchema);
export type GetStaffUsersResponseType = z.infer<typeof GetStaffUsersResponseSchema>;

export const GetStaffUsersQuerySchema = paginateQuerySchema({
  sortBy: ['firstName', 'lastName', 'role', 'status'] as const,
});
export type GetStaffUsersQueryInputType = z.input<typeof GetStaffUsersQuerySchema>;
export type GetStaffUsersQueryOutputType = z.output<typeof GetStaffUsersQuerySchema>;

export const CreateStaffUsersRequestSchema = StaffUserSchema.pick({
  email: true,
  firstName: true,
  lastName: true,
  role: true,
}).extend({
  permissions: StaffUserSchema.shape.permissions.optional(),
});
export type CreateStaffUsersRequestType = z.infer<typeof CreateStaffUsersRequestSchema>;

export const CreateStaffUsersResponseSchema = StaffUserSchema;
export type CreateStaffUsersResponseType = z.infer<typeof StaffUserSchema>;

export const UpdateStaffUsersRequestSchema = StaffUserSchema.pick({
  email: true,
  firstName: true,
  lastName: true,
  role: true,
  permissions: true,
}).partial();
export type UpdateStaffUsersRequestType = z.infer<typeof UpdateStaffUsersRequestSchema>;

export const UpdateStaffUsersResponseSchema = StaffUserSchema;
export type UpdateStaffUsersResponseType = z.infer<typeof UpdateStaffUsersResponseSchema>;
